// ============================================================================
// Instagram DM — outbound message / typing helpers.
//
// Every outbound targets `graph.instagram.com/v21.0/me/messages`. Meta accepts
// both `Authorization: Bearer <token>` AND `?access_token=...` on IG Graph,
// but we ALWAYS use the Bearer header so the token never hits Cloudflare edge
// / proxy logs.
//
// Two IG-specific constraints vs FB Messenger:
//   1. 24-hour window — the business may reply freely within 24h of the user's
//      last inbound message. Outside the window the caller MUST supply
//      `messaging_type: "MESSAGE_TAG"` + `tag: "HUMAN_AGENT"`. We auto-stamp
//      those when the caller flags `isOutside24hWindow: true` — otherwise the
//      Meta request returns a 10303/10304/2534022 subcode and we throw
//      `OUTSIDE_24H_WINDOW`.
//   2. Quick replies are capped at 13 buttons per message (vs 13 on FB too,
//      but IG rejects the request with a different subcode). We trim silently
//      so the caller does not have to.
//
// The `sendTyping` helper is advisory (swallows transport failures + non-2xx
// + typing indicators are cosmetic). `sendMessage` is strict: any non-2xx
// throws an `InstagramDmError` typed by `classifyInstagramError`.
// ============================================================================

import type { MessageAttachment } from '../../capabilities/messaging'
import { redactInstagramSecrets } from './oauth'
import {
  InstagramDmError,
  classifyInstagramError,
  parseInstagramRetryAfterMs,
  type InstagramErrorEnvelope,
  type InstagramSendResponse,
} from './types'

// ============================================================================
// Constants
// ============================================================================

/** Instagram Graph API base. v21.0 is the current LTS at time of writing. */
const INSTAGRAM_GRAPH_API_BASE = 'https://graph.instagram.com/v21.0'
const MESSAGES_ENDPOINT = `${INSTAGRAM_GRAPH_API_BASE}/me/messages`

/** IG caps quick-reply buttons at 13 per message. */
const QUICK_REPLY_MAX = 13

/** The ONLY message tag Meta allows for Instagram (non-policy-abuse case). */
const IG_MESSAGE_TAG = 'HUMAN_AGENT' as const

// ============================================================================
// Public types
// ============================================================================

export interface SendInstagramMessageInput {
  /** Recipient IGSID (17-digit numeric string). */
  recipientId: string
  text?: string
  attachments?: MessageAttachment[]
  /**
   * Hint from the caller: is the conversation currently OUTSIDE the 24h
   * response window? If true, the adapter auto-stamps `messaging_type`
   * + `tag: HUMAN_AGENT`. If false/undefined, the request goes out as a
   * regular RESPONSE — Meta will reject with OUTSIDE_24H_WINDOW if the
   * caller was wrong.
   */
  isOutside24hWindow?: boolean
  /** Override the messaging_type if the caller needs MESSAGE_TAG explicitly. */
  messagingType?: 'RESPONSE' | 'UPDATE' | 'MESSAGE_TAG'
  /** Override the tag — Meta only accepts `HUMAN_AGENT` on IG. */
  tag?: 'HUMAN_AGENT'
  /** Optional quick-reply buttons rendered below the message. */
  quickReplies?: Array<{ title: string; payload: string }>
}

export interface SendInstagramMessageOutput {
  providerMessageId: string
  sentAt: string
}

export interface SendInstagramTypingInput {
  recipientId: string
  state: 'on' | 'off'
  /**
   * Optional tenant identifiers threaded through for log correlation. When
   * provided they're stamped on the advisory swallowed-error console line so
   * operators can join the Workers tail log to a specific connector instance.
   * (Issue #249 M4.)
   */
  instanceId?: string
  companyId?: string
}

// ============================================================================
// sendInstagramMessage — strict (throws on error)
// ============================================================================

/**
 * POST a message to the Instagram Messaging API.
 *
 * Throws `InstagramDmError` on any non-2xx response; the dispatcher catches +
 * retries / surfaces to the UI. On success returns the IG `message_id` and a
 * client-captured ISO timestamp (Meta does not echo a server timestamp).
 */
export async function sendInstagramMessage(
  input: SendInstagramMessageInput,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SendInstagramMessageOutput> {
  validateRecipient(input.recipientId)
  validateMessagePayload(input)
  assertAccessToken(accessToken)

  const message = buildMessagePayload(input)
  const body: Record<string, unknown> = {
    recipient: { id: input.recipientId },
    message,
  }

  // Determine the messaging_type + tag combo.
  //   - Explicit overrides win.
  //   - `isOutside24hWindow: true` auto-stamps MESSAGE_TAG + HUMAN_AGENT.
  //   - Otherwise omit `messaging_type` (Meta defaults to RESPONSE on IG).
  const explicitType = input.messagingType
  const explicitTag = input.tag
  if (explicitType === 'MESSAGE_TAG' || explicitTag === IG_MESSAGE_TAG) {
    body.messaging_type = 'MESSAGE_TAG'
    body.tag = IG_MESSAGE_TAG
  } else if (explicitType) {
    body.messaging_type = explicitType
  } else if (input.isOutside24hWindow === true) {
    body.messaging_type = 'MESSAGE_TAG'
    body.tag = IG_MESSAGE_TAG
  }

  const response = await fetchImpl(MESSAGES_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Token in the Authorization header — never the URL. Graph / IG Graph
      // v11+ accept Bearer on every endpoint that would otherwise take
      // `access_token` as a query param, and the header form keeps the token
      // out of Cloudflare edge + proxy logs.
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw await errorFromResponse(response)
  }

  const parsed = (await response.json()) as InstagramSendResponse
  return {
    providerMessageId: parsed.message_id,
    // IG does not return a server timestamp on success; client capture is
    // sufficient for dedupe + UI ordering.
    sentAt: new Date().toISOString(),
  }
}

// ============================================================================
// sendInstagramTyping — advisory (swallows non-2xx / network errors)
// ============================================================================

/**
 * Show or hide the typing indicator on the IG conversation.
 *
 * IG accepts `sender_action: typing_on | typing_off` on the same
 * `/me/messages` endpoint. Typing indicators are cosmetic — a failure must
 * not break the conversation flow. We log + swallow non-2xx + thrown fetch
 * errors. `INVALID_PAYLOAD` (empty recipient) still propagates because that
 * signals a caller bug.
 */
export async function sendInstagramTyping(
  input: SendInstagramTypingInput,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  validateRecipient(input.recipientId)
  assertAccessToken(accessToken)
  if (input.state !== 'on' && input.state !== 'off') {
    throw new InstagramDmError(
      'INVALID_PAYLOAD',
      `sendInstagramTyping: state must be 'on' or 'off', got ${input.state}`,
    )
  }

  const body = {
    recipient: { id: input.recipientId },
    sender_action: input.state === 'on' ? 'typing_on' : 'typing_off',
  }

  // Issue #249 M4: thread tenant context into swallowed-error log lines so
  // operators can correlate Workers tail output back to a specific instance.
  const ctxSuffix = buildTypingLogSuffix(input.instanceId, input.companyId)

  try {
    const response = await fetchImpl(MESSAGES_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const err = await errorFromResponse(response)
      console.error(
        `[instagram-dm] sendTyping failed (swallowed): code=${err.code} httpStatus=${err.details.httpStatus ?? 'n/a'} providerCode=${err.details.providerCode ?? 'n/a'} fbtraceId=${err.details.fbtraceId ?? 'n/a'}${ctxSuffix}`,
      )
    }
  } catch (networkErr) {
    if (
      networkErr instanceof InstagramDmError &&
      networkErr.code === 'INVALID_PAYLOAD'
    ) {
      throw networkErr
    }
    const safeMessage =
      networkErr instanceof Error
        ? redactInstagramSecrets(networkErr.message)
        : 'unknown'
    console.error(
      `[instagram-dm] sendTyping network error (swallowed): ${safeMessage}${ctxSuffix}`,
    )
  }
}

/** Build a trailing ` instanceId=… companyId=…` suffix for the log line. */
function buildTypingLogSuffix(
  instanceId: string | undefined,
  companyId: string | undefined,
): string {
  const parts: string[] = []
  if (typeof instanceId === 'string' && instanceId.length > 0) {
    parts.push(`instanceId=${instanceId}`)
  }
  if (typeof companyId === 'string' && companyId.length > 0) {
    parts.push(`companyId=${companyId}`)
  }
  return parts.length > 0 ? ` ${parts.join(' ')}` : ''
}

// ============================================================================
// Internal: payload builder
// ============================================================================

interface InstagramAttachmentPayload {
  type: 'image' | 'video' | 'audio' | 'file'
  payload: { url: string; is_reusable: boolean }
}

interface InstagramQuickReply {
  content_type: 'text'
  title: string
  payload: string
}

interface InstagramOutboundMessage {
  text?: string
  attachment?: InstagramAttachmentPayload
  quick_replies?: InstagramQuickReply[]
}

function buildMessagePayload(input: SendInstagramMessageInput): InstagramOutboundMessage {
  const message: InstagramOutboundMessage = {}

  if (input.text && input.text.length > 0) {
    message.text = input.text
  }

  if (input.attachments && input.attachments.length > 0) {
    // IG, like Messenger, accepts a SINGLE `attachment` per message. Pick the
    // first media attachment; quick-reply attachments are surfaced separately.
    const media = input.attachments.find(
      (a): a is Exclude<MessageAttachment, { type: 'quick_replies' }> =>
        a.type !== 'quick_replies',
    )
    if (media) {
      message.attachment = {
        type: media.type,
        payload: { url: media.url, is_reusable: false },
      }
    }

    // Capability-level `quick_replies` attachments promote to the top-level
    // `message.quick_replies` array — capped at 13 per IG limits.
    const qr = input.attachments.find(
      (a): a is Extract<MessageAttachment, { type: 'quick_replies' }> =>
        a.type === 'quick_replies',
    )
    if (qr) {
      message.quick_replies = qr.options.slice(0, QUICK_REPLY_MAX).map((o) => ({
        content_type: 'text',
        title: o.label,
        payload: o.payload,
      }))
    }
  }

  if (input.quickReplies && input.quickReplies.length > 0) {
    // Explicit `quickReplies` from the SendMessageInput overrides any
    // quick-replies attachment — the explicit field is higher-intent.
    message.quick_replies = input.quickReplies.slice(0, QUICK_REPLY_MAX).map((o) => ({
      content_type: 'text',
      title: o.title,
      payload: o.payload,
    }))
  }

  return message
}

// ============================================================================
// Internal: error classifier
// ============================================================================

async function errorFromResponse(response: Response): Promise<InstagramDmError> {
  let envelope: InstagramErrorEnvelope | null = null
  try {
    envelope = (await response.json()) as InstagramErrorEnvelope
  } catch {
    // Meta occasionally returns HTML for edge gateway errors — keep envelope null.
  }
  const retryAfterMs = parseInstagramRetryAfterMs(response.headers.get('Retry-After'))
  return classifyInstagramError(envelope, response.status, retryAfterMs)
}

// ============================================================================
// Input validation
// ============================================================================

function validateRecipient(to: string | undefined | null): void {
  if (!to || typeof to !== 'string' || to.trim().length === 0) {
    throw new InstagramDmError(
      'INVALID_PAYLOAD',
      'recipient id is required (recipientId)',
    )
  }
}

function validateMessagePayload(input: SendInstagramMessageInput): void {
  const hasText = typeof input.text === 'string' && input.text.length > 0
  const hasAttachments = Array.isArray(input.attachments) && input.attachments.length > 0
  const hasQuickReplies = Array.isArray(input.quickReplies) && input.quickReplies.length > 0
  if (!hasText && !hasAttachments && !hasQuickReplies) {
    throw new InstagramDmError(
      'INVALID_PAYLOAD',
      'sendInstagramMessage: must supply text, attachments, or quickReplies',
    )
  }
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new InstagramDmError('INVALID_PAYLOAD', 'accessToken is required')
  }
}
